import { Navigate, Route, useLocation } from 'react-router-dom';
import { lazy, Suspense, type ReactNode } from 'react';
import './lpd-theme.css';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { PWAUpdateBanner } from './components/PWAUpdateBanner';
import { V2Disabled } from './components/V2Disabled';
import { LaadpleinErrorBoundary } from './components/LaadpleinErrorBoundary';
import { LpdLayout } from './components/LpdLayout';

const LaadpleinDesignerList = lazy(() => import('./pages/LaadpleinDesignerList'));
const LaadpleinDesignerDetail = lazy(() => import('./pages/LaadpleinDesignerDetail'));
const ScenarioEditor = lazy(() => import('./pages/ScenarioEditor'));
const KwartierAnalyse = lazy(() => import('./pages/KwartierAnalyse'));
const KavelEditor = lazy(() => import('./pages/KavelEditor'));
const CompareZwaagdijk = lazy(() => import('./pages/CompareZwaagdijk'));
const ProductCatalog = lazy(() => import('./pages/ProductCatalog'));
const Methodiek = lazy(() => import('./pages/Methodiek'));

/**
 * Feature flag — V2-routes alleen actief wanneer VITE_LPD_V2_ENABLED ≠ 'false'.
 * Default = enabled (huidige werking blijft). Stan kan via Vercel env-var
 * V2_ENABLED=false zetten om alle V2-paden door V2Disabled-placeholder te
 * laten renderen zonder code-deploy.
 */
const V2_ENABLED = import.meta.env.VITE_LPD_V2_ENABLED !== 'false';

const Loading = () => (
  <div className="w-full h-screen flex items-center justify-center text-gray-500">
    Laden…
  </div>
);

/**
 * V2Page — wrapper voor alle V2-routes. Mount de PWAUpdateBanner zodat
 * gebruikers automatisch een prompt krijgen wanneer er een nieuwe
 * service-worker-versie klaarstaat (anders blijft cache plakken na deploy).
 *
 * Bewust hier en niet in App.tsx → V1-pad blijft onaangeraakt
 * (zie docs/laadplein-designer/V1_INVENTORY.md).
 *
 * Wanneer V2_ENABLED=false rendert deze wrapper i.p.v. children een
 * minimale "V2 disabled" placeholder met een link naar V1.
 */
const V2Page = ({ children }: { children: ReactNode }) => {
  if (!V2_ENABLED) {
    return (
      <ProtectedRoute requiredRoles={['admin', 'super_admin', 'crm_user']}>
        <Suspense fallback={<Loading />}>
          <V2Disabled />
        </Suspense>
      </ProtectedRoute>
    );
  }
  return (
    <ProtectedRoute requiredRoles={['admin', 'super_admin', 'crm_user']}>
      {/* .lpd-v2 = GV-theme-scope (lpd-theme.css): primary/ring/accent in
          Greenvision-groen voor alle V2-oppervlakken; display:contents laat
          de layout ongemoeid. */}
      <div className="lpd-v2" style={{ display: 'contents' }}>
        <PWAUpdateBanner />
        <Suspense fallback={<Loading />}>{children}</Suspense>
      </div>
    </ProtectedRoute>
  );
};

/** Oude route-basis, van vóór de hernoeming naar Project Builder (T5.1). */
const LEGACY_BASE = '/laadplein-designer';

/**
 * LegacyBaseRedirect — stuurt elk oud /laadplein-designer-pad door naar het
 * gelijknamige pad onder /project-builder, inclusief query-string en hash.
 *
 * Bewust permanent en zonder env-vlag, in afwijking van trap 3 van de
 * sloopladder in de roadmap. Die trap dekt het wéghalen van functionaliteit af
 * en geeft je een vlag om terug te kunnen; bij een hernoeming verdwijnt er
 * niets. Een vlag zou hier alleen een stand toevoegen waarin bestaande
 * bookmarks, e-mail- en Slack-links doodlopen — precies wat de sloopladder
 * wil voorkomen. De oude URL blijft dus onvoorwaardelijk werken.
 *
 * Geen ProtectedRoute eromheen: de doelroute doet zelf de rolcontrole, en een
 * auth-gate op de redirect zou een niet-ingelogde bezoeker naar de login
 * sturen en zijn oorspronkelijke bestemming onderweg kwijtraken.
 *
 * replace houdt de oude URL uit de history, zodat "terug" niet meteen weer
 * op de redirect landt.
 */
const LegacyBaseRedirect = () => {
  const { pathname, search, hash } = useLocation();
  const rest = pathname.slice(LEGACY_BASE.length);
  return <Navigate to={`/project-builder${rest}${search}${hash}`} replace />;
};

/**
 * Project Builder routes (V2) — geïsoleerd in eigen feature-folder.
 * Wordt als fragment toegevoegd aan de root <Routes> in src/App.tsx.
 *
 * De folder- en exportnamen blijven bewust `laadplein-designer` /
 * `LaadpleinDesignerRoutes`: T5.1 hernoemt alleen wat de gebruiker ziet
 * (route, menu, UI-teksten), niet de codemappen of de tabellen.
 */
export const LaadpleinDesignerRoutes = (
  <>
    {/* Compare en editor-routes blijven full-screen (buiten CRM-layout) — */}
    {/* exact zoals V1 /crm/tekenaar/:projectId. Maximaal werkvlak voor map. */}
    <Route
      path="/project-builder/compare/zwaagdijk"
      element={
        <V2Page>
          <LaadpleinErrorBoundary area="lpd-compare">
            <CompareZwaagdijk />
          </LaadpleinErrorBoundary>
        </V2Page>
      }
    />
    <Route
      path="/project-builder/:designId/scenario/:scenarioId"
      element={
        <V2Page>
          <LaadpleinErrorBoundary area="lpd-editor">
            <ScenarioEditor />
          </LaadpleinErrorBoundary>
        </V2Page>
      }
    />
    <Route
      path="/project-builder/:designId/scenario/:scenarioId/kwartieranalyse"
      element={
        <V2Page>
          <LaadpleinErrorBoundary area="lpd-kwartieranalyse">
            <KwartierAnalyse />
          </LaadpleinErrorBoundary>
        </V2Page>
      }
    />
    <Route
      path="/project-builder/:designId/kavel"
      element={
        <V2Page>
          <LaadpleinErrorBoundary area="lpd-kavel">
            <KavelEditor />
          </LaadpleinErrorBoundary>
        </V2Page>
      }
    />

    {/* List + Detail in CRM-sidebar layout. Auth en PWA-banner via LpdLayout. */}
    {/* V2_ENABLED-fallback blijft via V2Page rond beide kinderen. */}
    <Route
      path="/project-builder"
      element={
        <V2Page>
          <LpdLayout />
        </V2Page>
      }
    >
      <Route index element={<LaadpleinDesignerList />} />
      <Route path="producten" element={<ProductCatalog />} />
      <Route path="methodiek" element={<Methodiek />} />
      <Route path=":id" element={<LaadpleinDesignerDetail />} />
    </Route>

    {/* Alles onder de oude basis, inclusief de basis zelf (splat mag leeg zijn). */}
    <Route path={`${LEGACY_BASE}/*`} element={<LegacyBaseRedirect />} />
  </>
);
